// Role → section visibility mapping (hardcoded per spec, no editor UI yet).
// Section keys correspond to sidebar nav ids and frontend route segments.
//
// Roles: platform_admin, owner, administrative, call_center, technician
// Legacy role names (gm_ops, dispatch_csr, kpi_viewer) are transparently mapped
// so any straggling JWTs keep working.

const LEGACY_MAP = {
  gm_ops: "administrative",
  dispatch_csr: "call_center",
  kpi_viewer: "technician",
};

export function normalizeRole(r) {
  return LEGACY_MAP[r] || r;
}

// true  = full access
// "limited" = restricted view (page renders but content is scoped)
// false = hidden
const MATRIX = {
  overview:       { platform_admin: true,  owner: true, administrative: true,      call_center: "limited", technician: false },
  agents:         { platform_admin: true,  owner: true, administrative: true,      call_center: false,     technician: false },
  kpi:            { platform_admin: true,  owner: true, administrative: true,      call_center: "entry",   technician: "own"  },
  l10:            { platform_admin: true,  owner: true, administrative: true,      call_center: false,     technician: false },
  cancellations:  { platform_admin: true,  owner: true, administrative: true,      call_center: true,      technician: false },
  dispatch_assist:{ platform_admin: true,  owner: true, administrative: true,      call_center: true,      technician: false },
  tech_profiles_view:{ platform_admin: true, owner: true, administrative: true,    call_center: true,      technician: false },
  thank_you_cards:{ platform_admin: true, owner: true, administrative: true,       call_center: true,      technician: false },
  hiring:         { platform_admin: true, owner: true, administrative: true,       call_center: true,      technician: false },
  collections:{ platform_admin: true, owner: true, administrative: true, gm_ops: true, call_center: true, technician: false },
  commissions:    { platform_admin: false, owner: false, administrative: false,     call_center: false,     technician: false },
  payroll:        { platform_admin: true,  owner: false, administrative: false,     call_center: false,     technician: false },
  csr_sales_log:  { platform_admin: true,  owner: true,  administrative: true,      call_center: true,      technician: false },
  comp:           { platform_admin: true,  owner: "read_only", administrative: false,    call_center: false,     technician: false },
  settings:       { platform_admin: true,  owner: "tenant_users", administrative: false, call_center: false, technician: false },
  admin_tenants:  { platform_admin: true,  owner: false, administrative: false,    call_center: false,     technician: false },
};

// pt.83.1 — email-allowlist aware Payroll access check.
// The role→section matrix hides Payroll from `owner` and `administrative`
// because those roles apply org-wide and Payroll is intentionally scoped
// to the small allowlist configured in backend env (PAYROLL_EDITOR_EMAILS,
// PAYROLL_APPROVER_EMAILS). This helper lets the sidebar say "yes, this
// user can access Payroll" when EITHER their role passes the matrix
// (platform_admin) OR their email is on the backend allowlist (delivered
// via the `payroll_access` field on /api/auth/me).
export function canAccessPayroll(user) {
  if (!user) return false;
  if (Array.isArray(user.payroll_access) && user.payroll_access.length > 0) return true;
  return canAccess(user.role, "payroll") === true;
}

// CC-261 — Administrator gets the widest access any tenant role has for a section. Sections whose
// non-platform access is an email allowlist on the backend (payroll, comp) stay as configured.
const TENANT_ROLES = ["owner", "administrative", "call_center", "technician"];
const ALLOWLIST_SECTIONS = new Set(["payroll", "comp", "commissions", "admin_tenants"]);

export function canAccess(role, section) {
  const r = normalizeRole(role);
  const row = MATRIX[section] || {};
  if (r === "administrative" && !ALLOWLIST_SECTIONS.has(section)) {
    const vals = TENANT_ROLES.map((k) => row[k]).filter(Boolean);
    if (vals.includes(true)) return true;
    return vals[0] || false;
  }
  const v = row[r];
  return v === undefined ? false : v;
}

export const ROLE_LABEL = {
  platform_admin: "Platform admin",
  owner: "Owner",
  administrative: "Administrative",
  call_center: "Call center / CSR",
  technician: "Technician",
};
